import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../api";
import AccountList from "../Components/Accounts/AccountList";
import AgedInvoicesList from "../Components/Accounts/InvoiceList";
import TransactionList from "../Components/Accounts/TransactionList";
import ClaimList from "../Components/Accounts/ClaimList";
import ERAList from "../Components/Accounts/ERAList";
import AllocationReport from "../Components/Accounts/AllocationReport";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

function Billing() {
  const [practice, setPractice] = useState([]);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [userRole, setUserRole] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get active section from URL, default to "account"
  const activeSection = searchParams.get("section") || "account";

  // sections config
  const sidebarItems = useMemo(
    () => [
      { key: "account", label: "Account List", icon: "fa-light fa-list" },
      {
        key: "invoices",
        label: "Open Invoices",
        gated: true,
        icon: "fa-sharp fa-light fa-file-invoice",
      },
      {
        key: "transactions",
        label: "Sales Report",
        gated: true,
        icon: "fa-light fa-cart-arrow-down",
      },
      {
        key: "allocations",
        label: "Allocation Report",
        gated: true,
        icon: "fa-sharp fa-light fa-ballot-check",
      },
      {
        key: "claims",
        label: "Claims",
        gated: true,
        icon: "fa-sharp fa-light fa-file-circle-exclamation",
      },
      {
        key: "eras",
        label: "ERA",
        gated: true,
        icon: "fa-sharp fa-light fa-atom",
      },
      // { key: "payments", label: "Ozow Payments", gated: true, icon: "fa-light fa-money-bill-wave" },
    ],
    [],
  );

  // Function to change section via URL
  const setActiveSection = (section) => {
    navigate(`/billing?section=${section}`, { replace: true });
  };

  // Handle legacy URL params (aging, paymentStatus, claims, eras)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("aging") && !params.get("section")) {
      navigate("/billing?section=invoices", { replace: true });
    }
    if (params.get("paymentStatus") && !params.get("section")) {
      navigate("/billing?section=payments", { replace: true });
    }
    if (params.get("claims") && !params.get("section")) {
      navigate("/billing?section=claims", { replace: true });
    }
    if (params.get("eras") && !params.get("section")) {
      navigate("/billing?section=eras", { replace: true });
    }
  }, [location.search, navigate]);

  // load user + roles
  useEffect(() => {
    async function getData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const user = jwtDecode(token);

        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];
        const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
        const allowedRoles = ["Finance", "SuperAdmin"];
        const hasAllowedRole = roles.some((r) => allowedRoles.includes(r));
        setUserRole(hasAllowedRole);

        const fetchedPractices = await getUserPracticeById(user._id);
        setPractice(Array.isArray(fetchedPractices) ? fetchedPractices : []);
      } catch (err) {
        console.error("⛔ Error fetching practices/roles:", err);
        setPractice([]);
        setUserRole(false);
      }
    }
    getData();
  }, []);

  // render header

  // render content
  function renderContent() {
    switch (activeSection) {
      case "account":
        return <AccountList />;
      case "invoices":
        return <AgedInvoicesList />;
      case "transactions":
        return <TransactionList />;
      case "allocations":
        return <AllocationReport />;
      case "claims":
        return <ClaimList />;
      case "eras":
        return <ERAList />;
      //   case "payments":
      //     return <OzowList />;
      default:
        return <AccountList />;
    }
  }

  return (
    <div className="ms-1" style={{ marginTop: "75px" }}>
      {/*  {renderHeader()} */}

      <Container fluid>
        <Row className="g-0">
          {/* LEFT PANEL */}
          <Col md={2} className="pt-1 g-0">
            <Container className="bg-white vh-100 g-0 p-3 shadow-sm rounded">
              <div className="text-center mb-3 mt-1">
                {sidebarItems.map((item) => {
                  if (item.gated && !userRole) return null; // hide if no role
                  return (
                    <div
                      key={item.key}
                      onMouseEnter={() =>
                        item.children && setHoveredSection(item.key)
                      }
                      onMouseLeave={() =>
                        item.children && setHoveredSection(null)
                      }
                    >
                      <Button
                        variant={
                          activeSection === item.key
                            ? "primary"
                            : "outline-primary"
                        }
                        className="w-100 text-start mb-1 d-flex align-items-center gap-2"
                        onClick={() => setActiveSection(item.key)}
                      >
                        {item.icon && (
                          <i className={item.icon} style={{ width: 18 }} />
                        )}
                        <span>{item.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col md={10}>
            <Container className="pt-3 bg-transparent pb-3 mb-3 rounded">
              {renderContent()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Billing;
